import { IPageSOBP, NeoStroke } from "@/neolab-libs/nl-lib3-common";
import ProjectSubmissionStore from "@/stores/ProjectSubmissionStore";



export interface USBBatchUploadItem {
    userCode: string;
    range: { start: IPageSOBP; end: IPageSOBP };
    strokes: NeoStroke[];
    submissionCode?: string;
}

export type UploadCallbacks = {
    onStart?: (total: number) => void;
    onItemStart?: (index: number, item: USBBatchUploadItem) => void;
    onItemSuccess?: (index: number, item: USBBatchUploadItem) => void;
    onItemError?: (index: number, item: USBBatchUploadItem, error: unknown) => void;
    onProgress?: (nextIndex: number, total: number, failed: number[]) => void;
    onComplete?: (result: { failedIndices: number[] }) => void;
    onCompleteHighLevel?: (status: string) => void;
    // Fired by processor exactly when USB stage completes (success or failure)
    onUsbStageComplete?: (successCount: number, failedIndices: number[]) => void;
};

export class USBBatchStrokeUploader {

    private projectCode: string;
    private projectSubmissionStore: ProjectSubmissionStore;

    private items: USBBatchUploadItem[];
    private progressKey: string;
    private nextIndex: number = 0;
    private callbacks: UploadCallbacks | undefined;
    
    private log(...args: any[]) {
        // Centralized logger for consistent prefixing
        // eslint-disable-next-line no-console
        console.log("[USBBatchStrokeUploader]", ...args);
    }
    
    
    constructor(
        projectCode: string,
        projectSubmissionStore: ProjectSubmissionStore,
        items: USBBatchUploadItem[],
        callbacks?: UploadCallbacks
    ) {
        this.projectCode = projectCode;
        this.projectSubmissionStore = projectSubmissionStore;
        this.items = items;
        this.progressKey = `USBBatchStrokeUploader:${this.projectCode}`;
        this.nextIndex = this.loadProgress();
        this.callbacks = callbacks;
        this.log("created", { projectCode: this.projectCode, items: this.items.length, nextIndex: this.nextIndex });
    }

    public setCallbacks(callbacks: UploadCallbacks) {
        this.callbacks = callbacks;
    }

    private loadProgress(): number {
        // In-memory progress only
        return this.nextIndex || 0;
    }

    private saveProgress(index: number) {
        // In-memory progress only
        this.nextIndex = index;
        this.log("saveProgress", { index });
    }

    public resetProgress() {
        // In-memory progress only
        this.nextIndex = 0;
        this.log("resetProgress");
    }

    public async resume(): Promise<{ allSucceeded: boolean; failedIndices: number[] }> {
        this.nextIndex = this.loadProgress();
        this.log("resume", { nextIndex: this.nextIndex });
        this.log("resume -> upload");
        return this.upload();
    }

    // Note: no separate retryFailed path since failures do not advance nextIndex

    // Instance function to generate rubric
    async upload(): Promise<{ allSucceeded: boolean; failedIndices: number[] }> {
        this.log("upload start", { totalItems: this.items.length, startIndex: this.nextIndex });
        this.callbacks?.onStart?.(this.items.length);
        for (let i = this.nextIndex; i < this.items.length; i++) {
            const item = this.items[i];
            let encounteredError = false;
            try {
                this.log("upload item", { index: i, userCode: item.userCode, strokes: item.strokes?.length ?? 0 });
                this.callbacks?.onItemStart?.(i, item);
                await this.projectSubmissionStore.uploadAnswerStrokes({
                    projectCode: this.projectCode,
                    userCode: item.userCode,
                    range: item.range,
                    strokes: item.strokes,
                    submissionCode: item.submissionCode
                });
                this.log("upload success", { index: i, userCode: item.userCode });
                this.callbacks?.onItemSuccess?.(i, item);
                // advance pointer only on success
                this.nextIndex = i + 1;
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error("[USBBatchStrokeUploader] upload failed for userCode=", item.userCode, e);
                this.log("upload marked failed", { index: i, userCode: item.userCode });
                this.callbacks?.onItemError?.(i, item, e);
                // do NOT advance pointer on error; persist current index
                this.nextIndex = i;
                encounteredError = true;
            }
            this.saveProgress(this.nextIndex);
            this.log("progress advanced", { nextIndex: this.nextIndex });
            this.callbacks?.onProgress?.(this.nextIndex, this.items.length, []);
            if (encounteredError) break;
        }

        // Completed all items; clear progress only if no failures remain
        if (this.nextIndex >= this.items.length) {
            this.log("upload complete; all succeeded");
            this.callbacks?.onComplete?.({ failedIndices: [] });
            return { allSucceeded: true, failedIndices: [] };
        } else {
            this.log("upload stopped due to failure", { nextIndex: this.nextIndex });
            this.callbacks?.onComplete?.({ failedIndices: [this.nextIndex] });
            return { allSucceeded: false, failedIndices: [this.nextIndex] };
        }
    }

}